import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  PieChart, Pie, Cell, Legend,
} from 'recharts';
import {
  TrendingUp, Package, AlertTriangle, CheckCircle2, Clock, Download,
  RefreshCw, Building2,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import { Card, CardContent } from '@/components/ui/card';
import { authHeaders } from '@/lib/authToken';
import { CsvDownloadButton } from '@/components/CsvDownloadButton';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

// Date helpers — local-day boundaries returned as plain `YYYY-MM-DD` strings
// (backend already inclusive-extends to end-of-day for date-only inputs).
const today = () => new Date().toISOString().slice(0, 10);
const daysAgo = (n) => {
  const d = new Date();
  d.setDate(d.getDate() - n);
  return d.toISOString().slice(0, 10);
};
const startOfMonth = (offset = 0) => {
  const d = new Date();
  d.setMonth(d.getMonth() + offset, 1);
  return d.toISOString().slice(0, 10);
};
const endOfMonth = (offset = 0) => {
  const d = new Date();
  d.setMonth(d.getMonth() + offset + 1, 0);
  return d.toISOString().slice(0, 10);
};

const DATE_PRESETS = [
  { value: 'today', label: 'Today', range: () => ({ s: today(), e: today() }) },
  { value: 'yesterday', label: 'Yesterday', range: () => ({ s: daysAgo(1), e: daysAgo(1) }) },
  { value: 'week', label: 'This Week', range: () => ({ s: daysAgo(6), e: today() }) },
  { value: 'month', label: 'This Month', range: () => ({ s: startOfMonth(), e: endOfMonth() }) },
  { value: 'last_month', label: 'Last Month', range: () => ({ s: startOfMonth(-1), e: endOfMonth(-1) }) },
  { value: 'all', label: 'All Time', range: () => ({ s: '', e: '' }) },
  { value: 'custom', label: 'Custom', range: () => null },
];

const PIE_COLORS = ['#10B981', '#F59E0B', '#EF4444', '#3B82F6', '#8B5CF6'];

function KpiCard({ icon: Icon, label, value, accent = 'blue', sub }) {
  const palettes = {
    blue: 'bg-blue-50 text-blue-700 ring-blue-200',
    emerald: 'bg-emerald-50 text-emerald-700 ring-emerald-200',
    amber: 'bg-amber-50 text-amber-700 ring-amber-200',
    red: 'bg-red-50 text-red-700 ring-red-200',
    violet: 'bg-violet-50 text-violet-700 ring-violet-200',
  };
  return (
    <Card className={`ring-1 ${palettes[accent]}`}>
      <CardContent className="p-4">
        <div className="flex items-center gap-2 mb-1">
          <Icon className="w-4 h-4" />
          <p className="text-xs uppercase tracking-wide font-semibold">{label}</p>
        </div>
        <p className="text-2xl font-bold" data-testid={`kpi-${label.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`}>{value}</p>
        {sub && <p className="text-[11px] opacity-70 mt-1">{sub}</p>}
      </CardContent>
    </Card>
  );
}

export default function FranchisePerformance() {
  const [preset, setPreset] = useState('month');
  const [startDate, setStartDate] = useState(startOfMonth());
  const [endDate, setEndDate] = useState(endOfMonth());
  const [sort, setSort] = useState('ordered_desc');
  const [summary, setSummary] = useState(null);
  const [rows, setRows] = useState([]);
  const [pending, setPending] = useState([]);
  const [loading, setLoading] = useState(true);
  const [drill, setDrill] = useState(null); // { franchise } when SKU drill open
  const [drillRows, setDrillRows] = useState([]);

  const applyPreset = (val) => {
    setPreset(val);
    const p = DATE_PRESETS.find((x) => x.value === val);
    if (p && p.value !== 'custom') {
      const r = p.range();
      setStartDate(r.s);
      setEndDate(r.e);
    }
  };

  const qs = useMemo(() => {
    const params = new URLSearchParams();
    if (startDate) params.set('start_date', startDate);
    if (endDate) params.set('end_date', endDate);
    return params.toString();
  }, [startDate, endDate]);

  const fetchAll = async () => {
    setLoading(true);
    try {
      const [s, t, p] = await Promise.all([
        axios.get(`${API}/franchise-performance/summary?${qs}`, { headers: authHeaders() }),
        axios.get(`${API}/franchise-performance/by-franchise?${qs}&sort=${sort}`, { headers: authHeaders() }),
        axios.get(`${API}/franchise-performance/pending-procurement`, { headers: authHeaders() }),
      ]);
      setSummary(s.data);
      setRows(t.data.rows || []);
      setPending(p.data.rows || []);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to load performance data');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchAll(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [qs, sort]);

  const openDrill = async (row) => {
    setDrill({ franchise: row });
    setDrillRows([]);
    try {
      const r = await axios.get(
        `${API}/franchise-performance/by-sku?franchise_id=${encodeURIComponent(row.franchise_id)}&${qs}`,
        { headers: authHeaders() },
      );
      setDrillRows(r.data.rows || []);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to load SKU drill-down');
    }
  };

  const downloadXlsx = async () => {
    try {
      const url = `${API}/franchise-performance/export.xlsx?${qs}&sort=${sort}`;
      const r = await axios.get(url, { headers: authHeaders(), responseType: 'blob' });
      const blob = new Blob([r.data], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
      const link = document.createElement('a');
      link.href = window.URL.createObjectURL(blob);
      link.download = `franchise-performance-${today()}.xlsx`;
      link.click();
      toast.success('Excel downloaded');
    } catch {
      toast.error('Export failed');
    }
  };

  const t = summary?.totals || {};
  // Pie data — line-count composition (invoiced / pending / partial / in-flight).
  const pieData = [
    { name: 'Invoiced', value: Number(t.invoiced_lines || 0) },
    { name: 'Pending Procurement', value: Number(t.pending_lines || 0) },
    { name: 'Partial', value: Number(t.partial_lines || 0) },
    { name: 'In-Flight', value: Number(t.in_flight_lines || 0) },
  ].filter((d) => d.value > 0);

  const top10 = (rows || []).slice().sort((a, b) => b.ordered_lines - a.ordered_lines).slice(0, 10)
    .map((r) => ({ name: (r.franchise_name || '—').slice(0, 14), Ordered: r.ordered_lines, Invoiced: r.invoiced_lines }));

  return (
    <div className="p-6 max-w-7xl mx-auto space-y-5" data-testid="franchise-performance-page">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div>
          <h1 className="text-2xl font-bold text-slate-800">Franchise Performance</h1>
          <p className="text-sm text-slate-500">Real-time order vs invoice fulfilment, pending procurement, and SKU drill-down.</p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <Select value={preset} onValueChange={applyPreset}>
            <SelectTrigger className="w-[140px]" data-testid="date-preset"><SelectValue /></SelectTrigger>
            <SelectContent>
              {DATE_PRESETS.map((p) => <SelectItem key={p.value} value={p.value}>{p.label}</SelectItem>)}
            </SelectContent>
          </Select>
          {preset === 'custom' && (
            <>
              <Input type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)} className="w-[150px]" data-testid="custom-start"/>
              <Input type="date" value={endDate} onChange={(e) => setEndDate(e.target.value)} className="w-[150px]" data-testid="custom-end"/>
            </>
          )}
          <Button onClick={fetchAll} variant="outline" size="sm" disabled={loading} data-testid="refresh-btn">
            <RefreshCw className={`w-4 h-4 mr-1 ${loading ? 'animate-spin' : ''}`} /> Refresh
          </Button>
          <Button onClick={downloadXlsx} size="sm" data-testid="export-xlsx-btn">
            <Download className="w-4 h-4 mr-1" /> Excel
          </Button>
          <CsvDownloadButton onDownload={downloadXlsx} testId="export-csv-btn" />
        </div>
      </div>

      {/* KPI Cards — SKU-line counts (not quantities) */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
        <KpiCard icon={Package} label="SKU Lines Ordered" value={Number(t.ordered_lines || 0).toLocaleString()} accent="blue" sub={`${t.order_count || 0} orders`}/>
        <KpiCard icon={CheckCircle2} label="SKU Lines Invoiced" value={Number(t.invoiced_lines || 0).toLocaleString()} accent="emerald"/>
        <KpiCard icon={Clock} label="SKU Lines Pending" value={Number(t.pending_lines || 0).toLocaleString()} accent="amber"/>
        <KpiCard icon={AlertTriangle} label="SKU Lines Partial" value={Number(t.partial_lines || 0).toLocaleString()} accent="violet" sub="Partly fulfilled"/>
        <KpiCard icon={TrendingUp} label="Fulfillment" value={`${(t.fulfillment_pct || 0).toFixed(1)}%`} accent="emerald" sub="invoiced ÷ ordered lines"/>
        <KpiCard icon={AlertTriangle} label="Pending %" value={`${(t.pending_pct || 0).toFixed(1)}%`} accent="red" sub="pending ÷ ordered lines"/>
      </div>

      {/* Charts row */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <Card>
          <CardContent className="p-4">
            <h3 className="font-semibold mb-2 text-sm text-slate-600">Top 10 Franchises — SKU Lines Ordered vs Invoiced</h3>
            {top10.length === 0 ? (
              <p className="text-xs text-slate-400 py-10 text-center">No data for the selected range.</p>
            ) : (
              <ResponsiveContainer width="100%" height={260}>
                <BarChart data={top10}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB"/>
                  <XAxis dataKey="name" fontSize={11} interval={0} angle={-25} textAnchor="end" height={60}/>
                  <YAxis fontSize={11}/>
                  <Tooltip/>
                  <Legend/>
                  <Bar dataKey="Ordered" fill="#3B82F6" radius={[4,4,0,0]}/>
                  <Bar dataKey="Invoiced" fill="#10B981" radius={[4,4,0,0]}/>
                </BarChart>
              </ResponsiveContainer>
            )}
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <h3 className="font-semibold mb-2 text-sm text-slate-600">SKU-Line Composition</h3>
            {pieData.length === 0 ? (
              <p className="text-xs text-slate-400 py-10 text-center">No quantities yet.</p>
            ) : (
              <ResponsiveContainer width="100%" height={260}>
                <PieChart>
                  <Pie data={pieData} dataKey="value" nameKey="name" innerRadius={60} outerRadius={95} paddingAngle={2}>
                    {pieData.map((_, i) => <Cell key={i} fill={PIE_COLORS[i % PIE_COLORS.length]}/>)}
                  </Pie>
                  <Tooltip/>
                  <Legend/>
                </PieChart>
              </ResponsiveContainer>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Franchise table */}
      <Card>
        <CardContent className="p-4">
          <div className="flex items-center justify-between mb-3">
            <h3 className="font-semibold text-slate-700"><Building2 className="w-4 h-4 inline mr-1"/>By Franchise ({rows.length})</h3>
            <Select value={sort} onValueChange={setSort}>
              <SelectTrigger className="w-[200px]" data-testid="sort-select"><SelectValue/></SelectTrigger>
              <SelectContent>
                <SelectItem value="ordered_desc">Highest SKU Lines Ordered</SelectItem>
                <SelectItem value="ordered_asc">Lowest SKU Lines Ordered</SelectItem>
                <SelectItem value="fulfillment_desc">Highest Fulfillment %</SelectItem>
                <SelectItem value="fulfillment_asc">Lowest Fulfillment %</SelectItem>
                <SelectItem value="pending_desc">Highest Pending Lines</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="franchise-table">
              <thead className="bg-slate-50 text-slate-600">
                <tr>
                  <th className="px-3 py-2 text-left">Franchise ID</th>
                  <th className="px-3 py-2 text-left">Name</th>
                  <th className="px-3 py-2 text-right">SKU Lines Ordered</th>
                  <th className="px-3 py-2 text-right">Invoiced</th>
                  <th className="px-3 py-2 text-right">Pending</th>
                  <th className="px-3 py-2 text-right">Partial</th>
                  <th className="px-3 py-2 text-right">Fulfilment %</th>
                  <th className="px-3 py-2 text-right">Pending %</th>
                  <th className="px-3 py-2"/>
                </tr>
              </thead>
              <tbody>
                {rows.length === 0 ? (
                  <tr><td colSpan={9} className="text-center py-8 text-slate-400">No franchises in this date range.</td></tr>
                ) : rows.map((r) => (
                  <tr key={r.franchise_id} className="border-t hover:bg-blue-50 cursor-pointer" onClick={() => openDrill(r)}
                      data-testid={`fp-row-${r.franchise_id}`}>
                    <td className="px-3 py-2 font-mono text-xs text-blue-700">{r.business_franchise_id || r.franchise_id?.slice(0, 8)}</td>
                    <td className="px-3 py-2">{r.franchise_name}</td>
                    <td className="px-3 py-2 text-right font-medium">{r.ordered_lines.toLocaleString()}</td>
                    <td className="px-3 py-2 text-right text-emerald-700">{r.invoiced_lines.toLocaleString()}</td>
                    <td className="px-3 py-2 text-right text-amber-700">{r.pending_lines.toLocaleString()}</td>
                    <td className="px-3 py-2 text-right text-violet-700">{r.partial_lines.toLocaleString()}</td>
                    <td className="px-3 py-2 text-right">
                      <span className={`px-2 py-0.5 rounded font-semibold ${r.fulfillment_pct >= 80 ? 'bg-emerald-100 text-emerald-700' : r.fulfillment_pct >= 50 ? 'bg-amber-100 text-amber-700' : 'bg-red-100 text-red-700'}`}>
                        {r.fulfillment_pct.toFixed(1)}%
                      </span>
                    </td>
                    <td className="px-3 py-2 text-right text-amber-700">{r.pending_pct.toFixed(1)}%</td>
                    <td className="px-3 py-2 text-right text-xs text-blue-600">Drill →</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>

      {/* Pending Procurement section */}
      <Card>
        <CardContent className="p-4">
          <h3 className="font-semibold text-slate-700 mb-3"><AlertTriangle className="w-4 h-4 inline mr-1 text-amber-600"/>Pending Procurement Demand</h3>
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="pending-procurement-table">
              <thead className="bg-amber-50 text-amber-800">
                <tr>
                  <th className="px-3 py-2 text-left">SKU</th>
                  <th className="px-3 py-2 text-left">Product</th>
                  <th className="px-3 py-2 text-right">Pending Qty</th>
                  <th className="px-3 py-2 text-right">Orders Affected</th>
                  <th className="px-3 py-2 text-right">Franchises Affected</th>
                </tr>
              </thead>
              <tbody>
                {pending.length === 0 ? (
                  <tr><td colSpan={5} className="text-center py-8 text-slate-400">No pending procurement — all current orders are allocated or invoiced ✓</td></tr>
                ) : pending.map((p) => (
                  <tr key={p.product_id} className="border-t hover:bg-amber-50">
                    <td className="px-3 py-2 font-mono text-xs">{p.product_sku}</td>
                    <td className="px-3 py-2">{p.product_name}</td>
                    <td className="px-3 py-2 text-right font-bold text-amber-700">{p.pending_qty.toLocaleString()}</td>
                    <td className="px-3 py-2 text-right">{p.affected_orders}</td>
                    <td className="px-3 py-2 text-right">{p.affected_franchises}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>

      {/* SKU Drill-down modal */}
      {drill && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4" onClick={() => setDrill(null)}>
          <div className="bg-white rounded-xl shadow-xl max-w-4xl w-full max-h-[80vh] overflow-y-auto p-6" onClick={(e) => e.stopPropagation()}
               data-testid="sku-drill-dialog">
            <div className="flex items-start justify-between mb-3">
              <div>
                <h3 className="text-lg font-bold">{drill.franchise.franchise_name}</h3>
                <p className="text-xs text-slate-500 font-mono">{drill.franchise.business_franchise_id || drill.franchise.franchise_id}</p>
              </div>
              <Button variant="ghost" onClick={() => setDrill(null)}>Close</Button>
            </div>
            <table className="w-full text-sm">
              <thead className="bg-slate-50">
                <tr>
                  <th className="px-3 py-2 text-left">SKU</th>
                  <th className="px-3 py-2 text-left">Product</th>
                  <th className="px-3 py-2 text-right">Lines</th>
                  <th className="px-3 py-2 text-right">Invoiced</th>
                  <th className="px-3 py-2 text-right">Pending</th>
                  <th className="px-3 py-2 text-right">Partial</th>
                  <th className="px-3 py-2 text-right">Ful %</th>
                  <th className="px-3 py-2 text-right">Total Qty</th>
                </tr>
              </thead>
              <tbody>
                {drillRows.length === 0 ? (
                  <tr><td colSpan={8} className="text-center py-6 text-slate-400">Loading…</td></tr>
                ) : drillRows.map((r) => (
                  <tr key={r.product_id} className="border-t">
                    <td className="px-3 py-2 font-mono text-xs">{r.product_sku}</td>
                    <td className="px-3 py-2">{r.product_name}</td>
                    <td className="px-3 py-2 text-right font-medium">{r.ordered_lines}</td>
                    <td className="px-3 py-2 text-right text-emerald-700">{r.invoiced_lines}</td>
                    <td className="px-3 py-2 text-right text-amber-700">{r.pending_lines}</td>
                    <td className="px-3 py-2 text-right text-violet-700">{r.partial_lines}</td>
                    <td className="px-3 py-2 text-right">{r.fulfillment_pct.toFixed(1)}%</td>
                    <td className="px-3 py-2 text-right text-slate-500">{r.ordered_qty.toLocaleString()}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}
